import { TypedDocumentNode, gql } from '@apollo/client';

import { useTypedParams } from '@sorare/routing';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';

import { Matches as DumbMatches } from '@sorare/gameplay/src/components/seo/Matches';

import { useLayoutData } from 'pages/players/$slug/layout';
import { playerFootballGameToGeneric } from 'pages/seo/player/utils';

import type {
  PlayerFixturesQuery,
  PlayerFixturesQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const PLAYER_FIXTURES_QUERY = gql`
  query PlayerFixturesQuery(
    $slug: String!
    $cursor: String
    $futureCursor: String
    $skipPastGames: Boolean!
    $skipFutureGames: Boolean!
  ) {
    football {
      player(slug: $slug) {
        slug
        futureGames(first: 100, after: $futureCursor)
          @skip(if: $skipFutureGames) {
          nodes {
            id
            ...playerFootballGameToGeneric_game
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
        pastGames(first: 20, after: $cursor) @skip(if: $skipPastGames) {
          nodes {
            id
            ...playerFootballGameToGeneric_game
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${playerFootballGameToGeneric.fragments.game}
` as TypedDocumentNode<PlayerFixturesQuery, PlayerFixturesQueryVariables>;

const Fixtures = () => {
  const openDialog = useOpenDialog();
  const { slug } = useTypedParams<PageParams>();
  const { player } = useLayoutData();
  const displayName = player?.displayName;

  const { data, loading, loadMore } = usePaginatedQuery(PLAYER_FIXTURES_QUERY, {
    variables: {
      slug: slug || '',
      skipPastGames: false,
      skipFutureGames: false,
    },
    connection: 'GameConnection',
    skip: !slug,
  });
  const { futureGames, pastGames } = data?.football.player || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        futureCursor: futureGames?.pageInfo.endCursor,
        cursor: pastGames?.pageInfo.endCursor,
        skipPastGames: !!futureGames?.pageInfo.hasNextPage,
        skipFutureGames: !futureGames?.pageInfo.hasNextPage,
      });
    },
    !!(
      futureGames?.pageInfo.hasNextPage ||
      pastGames?.pageInfo.hasNextPage ||
      !pastGames
    ),
    loading
  );

  return (
    <>
      <NoIndex />
      <DumbMatches
        displayName={displayName}
        loading={loading}
        futureGames={[...(futureGames?.nodes || [])]
          .reverse()
          .map(game => playerFootballGameToGeneric({ game }))}
        pastGames={(pastGames?.nodes || []).map(game =>
          playerFootballGameToGeneric({
            game,
            onClick: id => openDialog(DialogKey.game, id),
          })
        )}
        infiniteScrollLoader={<InfiniteScrollLoader />}
      />
    </>
  );
};

export default Fixtures;
